"use client"
import { useState, useRef } from "react"
import { Loader, Edit2, BookOpen } from "lucide-react"
import { postAws } from "../postAws"
import {
  postTeacherElearningCourseVideo,
  generatePresignedUrl,
  postJsonInS3OfYoutubeNotesTeacher,
} from "../postFileAws"
import { extractYoutubeVideoId } from "./ui-components"

export const ContentManagement = ({
  courseId,
  course,
  updateCourse,
  unsavedTopics,
  setUnsavedTopics,
  showToast,
  currentTopic,
  setCurrentTopic,
  activeContentTab,
  setActiveContentTab,
  previewMode,
  selectContent,
  fetchCourseDetails,
  handleCreateNotesFromVideo,
}) => {
  const [isContentModalOpen, setIsContentModalOpen] = useState(false)
  const [contentType, setContentType] = useState("video") // "video", "pdf", or "quiz"
  const [videoData, setVideoData] = useState({
    title: "",
    description: "",
    file: null,
    type: "video",
    videoType: "file", // "file" or "youtube"
    youtubeUrl: "",
    previewUrl: null,
  })
  const [pdfData, setPdfData] = useState({
    title: "",
    description: "",
    file: null,
    type: "pdf",
    previewUrl: null,
  })
  const [quizData, setQuizData] = useState({
    title: "",
    description: "",
    type: "quiz",
    questions: [
      {
        id: Date.now(),
        text: "",
        options: [
          { id: Date.now() + 1, text: "", isCorrect: false },
          { id: Date.now() + 2, text: "", isCorrect: false },
          { id: Date.now() + 3, text: "", isCorrect: false },
          { id: Date.now() + 4, text: "", isCorrect: false },
        ],
      },
    ],
  })
  const [isEditingSubtopic, setIsEditingSubtopic] = useState(false)
  const [editingSubtopicData, setEditingSubtopicData] = useState(null)
  const [isUpdatingSubtopic, setIsUpdatingSubtopic] = useState(false)
  const [uploadProgress, setUploadProgress] = useState(0)
  const [addingSubtopicId, setAddingSubtopicId] = useState(null)
  const [showContentMenu, setShowContentMenu] = useState(null)
  const [isDeletingSubtopic, setIsDeletingSubtopic] = useState(false)
  const [isConvertingJsonToPdf, setIsConvertingJsonToPdf] = useState(false)

  // New states for notes editing
  const [isEditingNotes, setIsEditingNotes] = useState(false)
  const [editingNotesData, setEditingNotesData] = useState(null)
  const [notesJsonData, setNotesJsonData] = useState(null)
  const [isLoadingNotesJson, setIsLoadingNotesJson] = useState(false)
  const [isSavingNotes, setIsSavingNotes] = useState(false)

  const fileInputRef = useRef(null)
  const pdfInputRef = useRef(null)
  const youtubeInputRef = useRef(null)
  const editFileInputRef = useRef(null)

  // Function to check if a note is AI-generated
  const isAIGeneratedNote = (item) => {
    return (
      (item.quizStatus === "completed" && item.type === "pdf") || (item.rawJsonUrl && item.rawJsonUrl.trim() !== "")
    )
  }

  // Function to open notes editor
  const openNotesEditor = async (item) => {
    if (!item.rawJsonUrl) {
      showToast("No JSON data available for editing", "error")
      return
    }

    setIsLoadingNotesJson(true)
    setIsEditingNotes(true)
    setEditingNotesData(item)

    try {
      // Generate presigned URL and fetch JSON content
      const presignedUrl = await generatePresignedUrl(item.rawJsonUrl)
      const response = await fetch(presignedUrl)

      if (!response.ok) {
        throw new Error("Failed to fetch notes data")
      }

      const jsonData = await response.json()
      setNotesJsonData(jsonData)
      showToast("Notes loaded successfully", "success")
    } catch (error) {
      console.error("Error loading notes JSON:", error)
      showToast("Error loading notes data", "error")
      setIsEditingNotes(false)
      setEditingNotesData(null)
    } finally {
      setIsLoadingNotesJson(false)
    }
  }

  // Function to close notes editor
  const closeNotesEditor = () => {
    setIsEditingNotes(false)
    setEditingNotesData(null)
    setNotesJsonData(null)
  }

  // Fixed function to update notes content
  const updateNotesContent = (noteIndex, field, value, subEntryIndex = null, updatedNotesArray = null) => {
    setNotesJsonData((prev) => {
      if (!prev) return prev

      // If we're updating the entire notes array
      if (updatedNotesArray) {
        return {
          ...prev,
          notes: updatedNotesArray,
        }
      }

      // If we're updating the notes array directly
      if (field === "notes") {
        return {
          ...prev,
          notes: value,
        }
      }

      // If we're updating sub_entries
      if (field === "sub_entries") {
        const updated = { ...prev }
        if (updated.notes && updated.notes[noteIndex]) {
          updated.notes[noteIndex].sub_entries = value
        }
        return updated
      }

      // Regular field updates
      const updated = { ...prev }
      if (!updated.notes) {
        updated.notes = []
      }

      if (subEntryIndex !== null) {
        // Updating sub-entry
        if (updated.notes[noteIndex] && updated.notes[noteIndex].sub_entries) {
          updated.notes[noteIndex].sub_entries[subEntryIndex][field] = value
        }
      } else {
        // Updating main note
        if (updated.notes[noteIndex]) {
          updated.notes[noteIndex][field] = value
        }
      }
      return updated
    })
  }

  // Function to update title
  const updateNotesTitle = (value) => {
    setNotesJsonData((prev) => ({
      ...prev,
      title: value,
    }))
  }

  // Function to save edited notes
  const saveEditedNotes = async () => {
    if (!notesJsonData || !editingNotesData) {
      showToast("No data to save", "error")
      return
    }

    setIsSavingNotes(true)
    try {
      showToast("Saving notes...", "info")

      // 1. Extract filename from original rawJsonUrl
      const originalRawJsonUrl = editingNotesData.rawJsonUrl
      const filename = originalRawJsonUrl.split("/").pop() // Get the last part after the last slash

      // 2. Get required parameters
      const teacherId = localStorage.getItem("teacherId")
      const classRoomId = localStorage.getItem("classRoomId")

      if (!teacherId || !classRoomId) {
        throw new Error("Teacher ID or Classroom ID not found")
      }

      // Find topic and subtopic details
      const allTopics = [...(course?.topics || []), ...unsavedTopics]
      const currentTopicObj = allTopics.find((t) => t.id === activeContentTab.topicId)
      if (!currentTopicObj) {
        throw new Error("Topic not found")
      }

      const coursetitle = course.name
      const topic = currentTopicObj.title
      const subtopic = editingNotesData.title

      // 3. Construct JSON in the same format as original
      const updatedJsonData = {
        title: notesJsonData.title,
        generated_at: notesJsonData.generated_at || new Date().toISOString(),
        course_info: notesJsonData.course_info || {
          courseTitle: coursetitle,
          topic: topic,
          subtopic: subtopic,
        },
        notes: notesJsonData.notes || [],
      }

      // 4. Convert JSON to file object
      const jsonString = JSON.stringify(updatedJsonData, null, 2)
      const jsonBlob = new Blob([jsonString], { type: "application/json" })
      const jsonFile = new File([jsonBlob], filename, { type: "application/json" })

      // 5. Save updated JSON to S3
      showToast("Uploading updated notes...", "info")
      const jsonUploadResult = await postJsonInS3OfYoutubeNotesTeacher(
        jsonFile,
        teacherId,
        coursetitle,
        topic,
        subtopic,
        filename,
      )

      if (!jsonUploadResult || !jsonUploadResult.uri) {
        throw new Error("Failed to upload updated JSON")
      }

      // 6. Generate new PDF from updated JSON
      showToast("Generating updated PDF...", "info")
      const pdfBlob = await convertJsonToPdf(updatedJsonData)

      // Create PDF file with appropriate name
      const pdfFileName = `${subtopic.replace(/[^a-zA-Z0-9]/g, "_")}_notes.pdf`
      const pdfFile = new File([pdfBlob], pdfFileName, { type: "application/pdf" })

      // 7. Save new PDF to S3
      showToast("Uploading updated PDF...", "info")
      const pdfUploadResult = await postTeacherElearningCourseVideo(
        pdfFile,
        teacherId,
        classRoomId,
        pdfFileName,
        coursetitle,
        topic,
        subtopic,
      )

      if (!pdfUploadResult || !pdfUploadResult.uri) {
        throw new Error("Failed to upload updated PDF")
      }

      // 8. Update database with new URLs
      showToast("Updating database...", "info")
      const updateResponse = await postAws("https://api-dev.aurait.ai/eLearning", "POST", {
        request: "edit-subtopic",
        body: {
          courseId: courseId,
          topicId: activeContentTab.topicId,
          subtopicId: editingNotesData.id,
          updates: {
            type: "pdf",
            pdf: {
              url: pdfUploadResult.uri,
            },
            rawJsonUrl: jsonUploadResult.uri,
          },
        },
      })

      if (updateResponse.statusCode !== 200) {
        throw new Error("Failed to update database")
      }

      // 9. Generate presigned URL for new PDF and update UI
      const newPresignedUrl = await generatePresignedUrl(pdfUploadResult.uri)

      // Update the course state with new URLs
      updateCourse((prev) => ({
        ...prev,
        topics: prev.topics.map((topic) => {
          if (topic.id === activeContentTab.topicId) {
            return {
              ...topic,
              content: topic.content.map((item) => {
                if (item.id === editingNotesData.id) {
                  return {
                    ...item,
                    url: newPresignedUrl,
                    s3Uri: pdfUploadResult.uri,
                    rawJsonUrl: jsonUploadResult.uri,
                  }
                }
                return item
              }),
            }
          }
          return topic
        }),
      }))

      // Update the editing notes data to reflect new PDF URL
      setEditingNotesData((prev) => ({
        ...prev,
        url: newPresignedUrl,
        s3Uri: pdfUploadResult.uri,
        rawJsonUrl: jsonUploadResult.uri,
      }))

      showToast("Notes saved and PDF updated successfully!", "success")
      window.location.reload()
    } catch (error) {
      console.error("Error saving notes:", error)
      showToast(`Error saving notes: ${error.message}`, "error")
    } finally {
      setIsSavingNotes(false)
    }
  }

  // Function to check and convert JSON to PDF
  const checkAndConvertJsonToPdf = async (subtopic, topicId) => {
    const s3Uri = subtopic.s3Uri || subtopic.url
    // Check if the S3 URI contains .json
    if (!s3Uri || !s3Uri.includes(".json")) {
      return false
    }

    try {
      setIsConvertingJsonToPdf(true)
      showToast("Converting notes to PDF format...", "info")
      // Fetch the JSON content from S3
      const presignedUrl = await generatePresignedUrl(s3Uri)
      const response = await fetch(presignedUrl)
      if (!response.ok) {
        throw new Error("Failed to fetch JSON content")
      }
      const jsonData = await response.json()
      // Convert JSON to PDF
      const pdfBlob = await convertJsonToPdf(jsonData)
      // Create a File object from the blob
      const pdfFile = new File([pdfBlob], `${subtopic.title.replace(/[^a-zA-Z0-9]/g, "_")}_notes.pdf`, {
        type: "application/pdf",
      })
      // Upload PDF to S3
      const teacherId = localStorage.getItem("teacherId")
      const classRoomId = localStorage.getItem("classRoomId")
      if (!teacherId || !classRoomId) {
        throw new Error("Teacher ID or Classroom ID not found")
      }
      // Find the topic title
      const allTopics = [...(course?.topics || []), ...unsavedTopics]
      const currentTopicObj = allTopics.find((t) => t.id === topicId)
      if (!currentTopicObj) {
        throw new Error("Chapter not found")
      }
      const uploadResult = await postTeacherElearningCourseVideo(
        pdfFile,
        teacherId,
        classRoomId,
        pdfFile.name,
        course.name,
        currentTopicObj.title,
        subtopic.title,
      )
      if (!uploadResult || !uploadResult.uri) {
        throw new Error("Failed to upload PDF")
      }
      // Update the subtopic with the new PDF URL
      const updateResponse = await postAws("https://api-dev.aurait.ai/eLearning", "POST", {
        request: "edit-subtopic",
        body: {
          courseId: courseId,
          topicId: topicId,
          subtopicId: subtopic.id,
          updates: {
            type: "pdf",
            pdf: {
              url: uploadResult.uri,
            },
          },
        },
      })
      if (updateResponse.statusCode === 200) {
        // Generate presigned URL for the new PDF
        const newPresignedUrl = await generatePresignedUrl(uploadResult.uri)
        // Update the course state
        updateCourse((prev) => ({
          ...prev,
          topics: prev.topics.map((topic) => {
            if (topic.id === topicId) {
              return {
                ...topic,
                content: topic.content.map((item) => {
                  if (item.id === subtopic.id) {
                    return {
                      ...item,
                      type: "pdf",
                      s3Uri: uploadResult.uri,
                      url: newPresignedUrl,
                      notesStatus: "completed",
                    }
                  }
                  return item
                }),
              }
            }
            return topic
          }),
        }))
        showToast("Notes converted to PDF successfully!", "success")
        return true
      } else {
        throw new Error("Failed to update subtopic with PDF")
      }
    } catch (error) {
      console.error("Error converting JSON to PDF:", error)
      showToast("Error converting notes to PDF. Please try again.", "error")
      return false
    } finally {
      setIsConvertingJsonToPdf(false)
    }
  }

  // Function to convert JSON notes to beautifully formatted PDF
  const convertJsonToPdf = async (jsonData) => {
    // Dynamic import of jsPDF
    const { jsPDF } = await import("jspdf")
    const doc = new jsPDF()
    let yPosition = 20
    const pageHeight = doc.internal.pageSize.height
    const margin = 20
    const maxWidth = doc.internal.pageSize.width - margin * 2
    // Helper function to add new page if needed
    const checkPageBreak = (requiredHeight) => {
      if (yPosition + requiredHeight > pageHeight - margin) {
        doc.addPage()
        yPosition = 20
      }
    }
    // Helper function to wrap text
    const wrapText = (text, maxWidth, fontSize = 12) => {
      doc.setFontSize(fontSize)
      return doc.splitTextToSize(text, maxWidth)
    }
    // Title
    doc.setFontSize(24)
    doc.setFont(undefined, "bold")
    doc.setTextColor(44, 62, 80) // Dark blue
    const titleLines = wrapText(jsonData.title || "Notes", maxWidth, 24)
    titleLines.forEach((line, index) => {
      checkPageBreak(10)
      doc.text(line, margin, yPosition)
      yPosition += 10
    })
    yPosition += 10
    // Course info
    if (jsonData.course_info) {
      doc.setFontSize(12)
      doc.setFont(undefined, "normal")
      doc.setTextColor(100, 100, 100)
      const courseInfo = [
        `Course: ${jsonData.course_info.courseTitle || ""}`,
        `Topic: ${jsonData.course_info.topic || ""}`,
        `Generated: ${new Date(jsonData.generated_at).toLocaleDateString() || ""}`,
      ]
      courseInfo.forEach((info) => {
        if (info.split(": ")[1]) {
          // Only show if there's content after the colon
          checkPageBreak(6)
          doc.text(info, margin, yPosition)
          yPosition += 6
        }
      })
      yPosition += 10
    }
    // Process notes
    if (jsonData.notes && Array.isArray(jsonData.notes)) {
      jsonData.notes.forEach((note, noteIndex) => {
        // Main heading
        checkPageBreak(15)
        doc.setFontSize(16)
        doc.setFont(undefined, "bold")
        doc.setTextColor(52, 73, 94) // Dark gray-blue
        const headingLines = wrapText(note.heading || "", maxWidth, 16)
        headingLines.forEach((line) => {
          checkPageBreak(8)
          doc.text(line, margin, yPosition)
          yPosition += 8
        })
        yPosition += 5
        // Main content
        if (note.content) {
          doc.setFontSize(11)
          doc.setFont(undefined, "normal")
          doc.setTextColor(0, 0, 0)
          const contentLines = wrapText(note.content, maxWidth, 11)
          contentLines.forEach((line) => {
            checkPageBreak(6)
            doc.text(line, margin, yPosition)
            yPosition += 6
          })
          yPosition += 8
        }
        // Sub entries
        if (note.sub_entries && Array.isArray(note.sub_entries)) {
          note.sub_entries.forEach((subEntry, subIndex) => {
            // Sub heading
            if (subEntry.heading) {
              checkPageBreak(10)
              doc.setFontSize(13)
              doc.setFont(undefined, "bold")
              doc.setTextColor(41, 128, 185) // Blue
              const subHeadingLines = wrapText(subEntry.heading, maxWidth - 10, 13)
              subHeadingLines.forEach((line) => {
                checkPageBreak(7)
                doc.text(line, margin + 10, yPosition)
                yPosition += 7
              })
              yPosition += 3
            }
            // Sub content
            if (subEntry.content) {
              doc.setFontSize(10)
              doc.setFont(undefined, "normal")
              doc.setTextColor(0, 0, 0)
              const subContentLines = wrapText(subEntry.content, maxWidth - 20, 10)
              subContentLines.forEach((line) => {
                checkPageBreak(5)
                doc.text(line, margin + 20, yPosition)
                yPosition += 5
              })
              yPosition += 6
            }
            // Nested sub entries (if any)
            if (subEntry.sub_entries && Array.isArray(subEntry.sub_entries)) {
              subEntry.sub_entries.forEach((nestedEntry) => {
                if (nestedEntry.heading) {
                  checkPageBreak(8)
                  doc.setFontSize(11)
                  doc.setFont(undefined, "bold")
                  doc.setTextColor(155, 89, 182) // Purple
                  const nestedHeadingLines = wrapText(nestedEntry.heading, maxWidth - 30, 11)
                  nestedHeadingLines.forEach((line) => {
                    checkPageBreak(6)
                    doc.text(line, margin + 30, yPosition)
                    yPosition += 6
                  })
                  yPosition += 2
                }
                if (nestedEntry.content) {
                  doc.setFontSize(9)
                  doc.setFont(undefined, "normal")
                  doc.setTextColor(0, 0, 0)
                  const nestedContentLines = wrapText(nestedEntry.content, maxWidth - 40, 9)
                  nestedContentLines.forEach((line) => {
                    checkPageBreak(4)
                    doc.text(line, margin + 40, yPosition)
                    yPosition += 4
                  })
                  yPosition += 5
                }
              })
            }
          })
        }
        // Add spacing between main notes
        yPosition += 10
      })
    }
    // Add footer with page numbers
    const pageCount = doc.internal.getNumberOfPages()
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i)
      doc.setFontSize(8)
      doc.setTextColor(150, 150, 150)
      doc.text(`Page ${i} of ${pageCount}`, doc.internal.pageSize.width - 30, doc.internal.pageSize.height - 10)
    }
    return doc.output("blob")
  }

  const openContentMenu = (topicId) => {
    // Make sure we have a valid topic ID
    if (topicId) {
      console.log("Opening content menu for topic:", topicId)
      setCurrentTopic(topicId)
      setShowContentMenu(`add-${topicId}`)
    } else {
      console.error("Attempted to open content menu with invalid topic ID")
    }
  }

  const openContentModal = (type) => {
    setContentType(type)
    setIsContentModalOpen(true)
    setShowContentMenu(null)
    // Reset all content data
    setVideoData({
      title: "",
      description: "",
      file: null,
      type: "video",
      videoType: "file",
      youtubeUrl: "",
      previewUrl: null,
    })
    setPdfData({
      title: "",
      description: "",
      file: null,
      type: "pdf",
      previewUrl: null,
    })
    setQuizData({
      title: "",
      description: "",
      type: "quiz",
      questions: [
        {
          id: Date.now(),
          text: "",
          options: [
            { id: Date.now() + 1, text: "", isCorrect: false },
            { id: Date.now() + 2, text: "", isCorrect: false },
            { id: Date.now() + 3, text: "", isCorrect: false },
            { id: Date.now() + 4, text: "", isCorrect: false },
          ],
        },
      ],
    })
    setUploadProgress(0)
  }

  const closeContentModal = () => {
    setIsContentModalOpen(false)
    setContentType("video")
    // Revoke any object URLs to prevent memory leaks
    if (videoData.previewUrl && videoData.previewUrl.startsWith("blob:")) {
      URL.revokeObjectURL(videoData.previewUrl)
    }
    if (pdfData.previewUrl && pdfData.previewUrl.startsWith("blob:")) {
      URL.revokeObjectURL(pdfData.previewUrl)
    }
  }

  const handleVideoFileChange = (e) => {
    const file = e.target.files[0]
    if (!file) return
    // Check if file is a video
    if (!file.type.startsWith("video/")) {
      showToast("Please upload a valid video file", "error")
      return
    }
    // Create a preview URL for the video
    const previewUrl = URL.createObjectURL(file)
    setVideoData({
      ...videoData,
      file,
      previewUrl,
    })
  }

  const handlePdfFileChange = (e) => {
    const file = e.target.files[0]
    if (!file) return
    // Check if file is a PDF
    if (file.type !== "application/pdf") {
      showToast("Please upload a valid PDF file", "error")
      return
    }
    // Create a preview URL for the PDF
    const previewUrl = URL.createObjectURL(file)
    setPdfData({
      ...pdfData,
      file,
      previewUrl,
    })
  }

  const handleQuizQuestionChange = (questionId, field, value) => {
    setQuizData({
      ...quizData,
      questions: quizData.questions.map((q) => (q.id === questionId ? { ...q, [field]: value } : q)),
    })
  }

  const handleQuizOptionChange = (questionId, optionId, field, value) => {
    setQuizData({
      ...quizData,
      questions: quizData.questions.map((q) => {
        if (q.id === questionId) {
          return {
            ...q,
            options: q.options.map((opt) => {
              if (opt.id === optionId) {
                if (field === "isCorrect" && value === true) {
                  // If setting this option as correct, make all other options incorrect
                  return { ...opt, isCorrect: true }
                }
                return { ...opt, [field]: value }
              } else if (field === "isCorrect" && value === true) {
                // If setting another option as correct, make this one incorrect
                return { ...opt, isCorrect: false }
              }
              return opt
            }),
          }
        }
        return q
      }),
    })
  }

  const addQuizQuestion = () => {
    const newQuestion = {
      id: Date.now(),
      text: "",
      options: [
        { id: Date.now() + 1, text: "", isCorrect: false },
        { id: Date.now() + 2, text: "", isCorrect: false },
        { id: Date.now() + 3, text: "", isCorrect: false },
        { id: Date.now() + 4, text: "", isCorrect: false },
      ],
    }
    setQuizData({
      ...quizData,
      questions: [...quizData.questions, newQuestion],
    })
  }

  const removeQuizQuestion = (questionId) => {
    if (quizData.questions.length <= 1) {
      showToast("Quiz must have at least one question", "error")
      return
    }
    setQuizData({
      ...quizData,
      questions: quizData.questions.filter((q) => q.id !== questionId),
    })
  }

  const handleVideoSubmit = async (e) => {
    e.preventDefault()
    if (videoData.videoType === "file" && !videoData.file) {
      showToast("Please select a video file to upload", "error")
      return
    }
    if (videoData.videoType === "youtube" && !videoData.youtubeUrl) {
      showToast("Please enter a YouTube URL", "error")
      return
    }
    // For YouTube videos, validate the URL
    if (videoData.videoType === "youtube") {
      const videoId = extractYoutubeVideoId(videoData.youtubeUrl)
      if (!videoId) {
        showToast("Invalid YouTube URL. Please enter a valid YouTube URL", "error")
        return
      }
    }
    setAddingSubtopicId(currentTopic)
    try {
      // Get necessary data from localStorage and state
      const teacherId = localStorage.getItem("teacherId")
      const classRoomId = localStorage.getItem("classRoomId")
      if (!teacherId || !classRoomId) {
        throw new Error("Teacher ID or Classroom ID not found")
      }
      // Find the topic title
      const allTopics = [...course.topics, ...unsavedTopics]
      const currentTopicObj = allTopics.find((t) => t.id === currentTopic)
      if (!currentTopicObj) {
        throw new Error("Chapter not found")
      }
      let uploadResult = null
      let presignedUrl = null
      // Handle file upload or YouTube URL
      if (videoData.videoType === "file") {
        // Upload the video file to S3
        setUploadProgress(10) // Start progress indicator
        uploadResult = await postTeacherElearningCourseVideo(
          videoData.file,
          teacherId,
          classRoomId,
          videoData.file.name,
          course.name,
          currentTopicObj.title,
          videoData.title,
        )
        setUploadProgress(70) // Update progress
        if (!uploadResult || !uploadResult.uri) {
          throw new Error("Failed to upload video")
        }
        // Generate a presigned URL for preview
        try {
          presignedUrl = await generatePresignedUrl(uploadResult.uri)
          setUploadProgress(90) // Update progress
        } catch (error) {
          console.error("Error generating presigned URL:", error)
          // Continue with the S3 URI if presigned URL fails
        }
      }
      // Now add the subtopic with the video information
      const response = await postAws("https://api-dev.aurait.ai/eLearning", "POST", {
        request: "add-subtopic",
        body: {
          courseId: courseId,
          topicId: currentTopic,
          subtopicTitle: videoData.title,
          subtopicDescription: videoData.description,
          videoUrl: videoData.videoType === "file" ? uploadResult.uri : videoData.youtubeUrl, // Store the S3 URI or YouTube URL
          contentType: "video",
          isYoutubeVideo: videoData.videoType === "youtube", // Add this flag to identify YouTube videos
        },
      })
      setUploadProgress(100) // Complete progress
      if (response.statusCode === 200) {
        const responseBody = JSON.parse(response.body)
        const newContent = {
          id: responseBody.subtopic.id,
          title: videoData.title,
          description: videoData.description,
          url: videoData.videoType === "file" ? presignedUrl || uploadResult.url : videoData.youtubeUrl, // Use presigned URL for display if available
          s3Uri: videoData.videoType === "file" ? uploadResult.uri : null, // Store the S3 URI for future reference
          type: "video",
          isYoutubeVideo: videoData.videoType === "youtube",
          youtubeVideoId: videoData.videoType === "youtube" ? extractYoutubeVideoId(videoData.youtubeUrl) : null,
          file: videoData.videoType === "file" ? videoData.file.name : null, // Store the filename for display purposes
        }
        if (currentTopic.startsWith("unsaved-")) {
          setUnsavedTopics(
            unsavedTopics.map((topic) =>
              topic.id === currentTopic ? { ...topic, content: [...(topic.content || []), newContent] } : topic,
            ),
          )
        } else {
          updateCourse((prev) => ({
            ...prev,
            topics: prev.topics.map((topic) => {
              if (topic.id === currentTopic) {
                return {
                  ...topic,
                  content: [...(topic.content || []), newContent],
                }
              }
              return topic
            }),
          }))
        }
        showToast(videoData.videoType === "file" ? "Video added successfully" : "YouTube video added successfully")
      } else {
        throw new Error(response.body.message || "Failed to add video")
      }
    } catch (error) {
      console.error("Error adding video:", error)
      showToast("Error adding video. Please try again.", "error")
      setUploadProgress(0) // Reset progress on error
    } finally {
      setAddingSubtopicId(null)
      closeContentModal()
    }
  }

  const handlePdfSubmit = async (e) => {
    e.preventDefault()
    if (!pdfData.file) {
      showToast("Please select a PDF file to upload", "error")
      return
    }
    setAddingSubtopicId(currentTopic)
    try {
      // Get necessary data from localStorage and state
      const teacherId = localStorage.getItem("teacherId")
      const classRoomId = localStorage.getItem("classRoomId")
      if (!teacherId || !classRoomId) {
        throw new Error("Teacher ID or Classroom ID not found")
      }
      // Find the topic title
      const allTopics = [...course.topics, ...unsavedTopics]
      const currentTopicObj = allTopics.find((t) => t.id === currentTopic)
      if (!currentTopicObj) {
        throw new Error("Chapter not found")
      }
      // Upload the PDF file to S3 using the same function as videos
      setUploadProgress(10) // Start progress indicator
      const uploadResult = await postTeacherElearningCourseVideo(
        pdfData.file,
        teacherId,
        classRoomId,
        pdfData.file.name,
        course.name,
        currentTopicObj.title,
        pdfData.title,
      )
      setUploadProgress(70) // Update progress
      if (!uploadResult || !uploadResult.uri) {
        throw new Error("Failed to upload PDF")
      }
      // Generate a presigned URL for preview
      let presignedUrl = null
      try {
        presignedUrl = await generatePresignedUrl(uploadResult.uri)
        setUploadProgress(90) // Update progress
      } catch (error) {
        console.error("Error generating presigned URL:", error)
        // Continue with the S3 URI if presigned URL fails
      }
      // Now add the subtopic with the PDF information
      const response = await postAws("https://api-dev.aurait.ai/eLearning", "POST", {
        request: "add-subtopic",
        body: {
          courseId: courseId,
          topicId: currentTopic,
          subtopicTitle: pdfData.title,
          subtopicDescription: pdfData.description,
          pdfUrl: uploadResult.uri, // Store the S3 URI in the database
          contentType: "pdf",
        },
      })
      setUploadProgress(100) // Complete progress
      if (response.statusCode === 200) {
        const responseBody = JSON.parse(response.body)
        const newContent = {
          id: responseBody.subtopic.id,
          title: pdfData.title,
          description: pdfData.description,
          url: presignedUrl || uploadResult.url, // Use presigned URL for display if available
          s3Uri: uploadResult.uri, // Store the S3 URI for future reference
          type: "pdf",
          file: pdfData.file.name, // Store the filename for display purposes
        }
        if (currentTopic.startsWith("unsaved-")) {
          setUnsavedTopics(
            unsavedTopics.map((topic) =>
              topic.id === currentTopic ? { ...topic, content: [...(topic.content || []), newContent] } : topic,
            ),
          )
        } else {
          updateCourse((prev) => ({
            ...prev,
            topics: prev.topics.map((topic) => {
              if (topic.id === currentTopic) {
                return {
                  ...topic,
                  content: [...(topic.content || []), newContent],
                }
              }
              return topic
            }),
          }))
        }
        showToast("PDF added successfully")
      } else {
        throw new Error(response.body.message || "Failed to add PDF")
      }
    } catch (error) {
      console.error("Error adding PDF:", error)
      showToast("Error adding PDF. Please try again.", "error")
      setUploadProgress(0) // Reset progress on error
    } finally {
      setAddingSubtopicId(null)
      closeContentModal()
    }
  }

  const handleQuizSubmit = async (e) => {
    e.preventDefault()
    // Validate quiz data
    if (!quizData.title.trim()) {
      showToast("Please enter a quiz title", "error")
      return
    }
    // Check if all questions have text and at least one correct answer
    const invalidQuestions = quizData.questions.filter((q) => !q.text.trim() || !q.options.some((opt) => opt.isCorrect))
    if (invalidQuestions.length > 0) {
      showToast("All questions must have text and at least one correct answer", "error")
      return
    }
    // Check if all options have text
    const invalidOptions = quizData.questions.flatMap((q) => q.options.filter((opt) => !opt.text.trim()))
    if (invalidOptions.length > 0) {
      showToast("All options must have text", "error")
      return
    }
    setAddingSubtopicId(currentTopic)
    try {
      // Calculate total marks (1 mark per question by default)
      const totalMarks = quizData.questions.length
      // Now add the subtopic with the quiz information
      const response = await postAws("https://api-dev.aurait.ai/eLearning", "POST", {
        request: "add-subtopic",
        body: {
          courseId: courseId,
          topicId: currentTopic,
          subtopicTitle: quizData.title,
          subtopicDescription: quizData.description,
          contentType: "quiz",
          quizData: JSON.stringify(quizData.questions),
          totalMarks: totalMarks,
        },
      })
      if (response.statusCode === 200) {
        const responseBody = JSON.parse(response.body)
        const newContent = {
          id: responseBody.subtopic.id,
          title: quizData.title,
          description: quizData.description,
          type: "quiz",
          quizData: quizData.questions,
          totalMarks: totalMarks,
        }
        if (currentTopic.startsWith("unsaved-")) {
          setUnsavedTopics(
            unsavedTopics.map((topic) =>
              topic.id === currentTopic ? { ...topic, content: [...(topic.content || []), newContent] } : topic,
            ),
          )
        } else {
          updateCourse((prev) => ({
            ...prev,
            topics: prev.topics.map((topic) => {
              if (topic.id === currentTopic) {
                return {
                  ...topic,
                  content: [...(topic.content || []), newContent],
                }
              }
              return topic
            }),
          }))
        }
        showToast("Quiz added successfully")
      } else {
        throw new Error(response.body.message || "Failed to add quiz")
      }
    } catch (error) {
      console.error("Error adding quiz:", error)
      showToast("Error adding quiz. Please try again.", "error")
    } finally {
      setAddingSubtopicId(null)
      closeContentModal()
    }
  }

  const handleDeleteSubtopic = async (topicId, subtopicId) => {
    if (window.confirm("Are you sure you want to delete this item?")) {
      setIsDeletingSubtopic(true)
      try {
        const response = await postAws("https://api-dev.aurait.ai/eLearning", "POST", {
          request: "delete-subtopic",
          body: {
            courseId: courseId,
            topicId: topicId,
            subtopicId: subtopicId,
          },
        })
        if (response.statusCode === 200) {
          updateCourse((prev) => ({
            ...prev,
            topics: prev.topics.map((topic) => {
              if (topic.id === topicId) {
                return {
                  ...topic,
                  content: topic.content.filter((item) => item.id !== subtopicId),
                }
              }
              return topic
            }),
          }))
          showToast("Item deleted successfully")
        } else {
          throw new Error(response.body.message || "Failed to delete item")
        }
      } catch (error) {
        console.error("Error deleting item:", error)
        showToast("Error deleting item. Please try again.", "error")
      } finally {
        setIsDeletingSubtopic(false)
      }
    }
  }

  // Add this function to handle opening the edit subtopic modal
  const openEditSubtopicModal = (topicId, subtopic) => {
    setCurrentTopic(topicId)
    // Create a copy of the subtopic data for editing
    const subtopicData = {
      id: subtopic.id,
      title: subtopic.title,
      description: subtopic.description || "",
      type: subtopic.type,
      // For videos
      videoType: subtopic.isYoutubeVideo ? "youtube" : "file",
      youtubeUrl: subtopic.isYoutubeVideo ? subtopic.url : "",
      s3Uri: !subtopic.isYoutubeVideo ? subtopic.s3Uri : null,
      // For PDFs
      pdfUrl: subtopic.type === "pdf" ? subtopic.s3Uri : null,
      // For quizzes
      quizData: subtopic.type === "quiz" ? subtopic.quizData : null,
      totalMarks: subtopic.totalMarks || 0,
      // New fields for file handling
      newFile: null,
      hasFileChanged: false,
      typeChanged: false, // Add this flag to track type changes
      originalType: subtopic.type, // Store the original type
    }
    setEditingSubtopicData(subtopicData)
    setIsEditingSubtopic(true)
    setUploadProgress(0)
  }

  // Add this function to handle closing the edit subtopic modal
  const closeEditSubtopicModal = () => {
    setIsEditingSubtopic(false)
    setEditingSubtopicData(null)
  }

  // Add a function to handle content type change
  const handleContentTypeChange = (newType) => {
    if (!editingSubtopicData) return
    setEditingSubtopicData({
      ...editingSubtopicData,
      type: newType,
      typeChanged: editingSubtopicData.originalType !== newType,
      // Reset file-specific fields when changing types
      newFile: null,
      hasFileChanged: true, // Mark as changed since we need a new file for the new type
    })
  }

  // Add a function to handle file input changes for the edit modal
  const handleEditFileChange = (e) => {
    const file = e.target.files[0]
    if (!file) return
    // Check if file type matches the current content type
    if (editingSubtopicData.type === "video" && !file.type.startsWith("video/")) {
      showToast("Please upload a valid video file", "error")
      return
    } else if (editingSubtopicData.type === "pdf" && file.type !== "application/pdf") {
      showToast("Please upload a valid PDF file", "error")
      return
    }
    setEditingSubtopicData({
      ...editingSubtopicData,
      newFile: file,
      hasFileChanged: true,
    })
  }

  // Add this function to handle updating the editing subtopic data
  const handleUpdateEditingData = (updatedData) => {
    setEditingSubtopicData(updatedData)
  }

  // Add this function to handle quiz question changes in edit mode
  const handleEditQuizQuestionChange = (questionId, field, value) => {
    setEditingSubtopicData((prev) => ({
      ...prev,
      quizData: prev.quizData.map((q) => (q.id === questionId ? { ...q, [field]: value } : q)),
    }))
  }

  // Add this function to handle quiz option changes in edit mode
  const handleEditQuizOptionChange = (questionId, optionId, field, value) => {
    setEditingSubtopicData((prev) => ({
      ...prev,
      quizData: prev.quizData.map((q) => {
        if (q.id === questionId) {
          return {
            ...q,
            options: q.options.map((opt) => {
              if (opt.id === optionId) {
                if (field === "isCorrect" && value === true) {
                  // If setting this option as correct, make all other options incorrect
                  return { ...opt, isCorrect: true }
                }
                return { ...opt, [field]: value }
              } else if (field === "isCorrect" && value === true) {
                // If setting another option as correct, make this one incorrect
                return { ...opt, isCorrect: false }
              }
              return opt
            }),
          }
        }
        return q
      }),
    }))
  }

  // Add this function to add a quiz question in edit mode
  const addEditQuizQuestion = () => {
    const newQuestion = {
      id: Date.now(),
      text: "",
      options: [
        { id: Date.now() + 1, text: "", isCorrect: false },
        { id: Date.now() + 2, text: "", isCorrect: false },
        { id: Date.now() + 3, text: "", isCorrect: false },
        { id: Date.now() + 4, text: "", isCorrect: false },
      ],
    }
    setEditingSubtopicData((prev) => ({
      ...prev,
      quizData: [...prev.quizData, newQuestion],
      totalMarks: prev.totalMarks + 1, // Increment total marks
    }))
  }

  // Add this function to remove a quiz question in edit mode
  const removeEditQuizQuestion = (questionId) => {
    if (editingSubtopicData.quizData.length <= 1) {
      showToast("Quiz must have at least one question", "error")
      return
    }
    setEditingSubtopicData((prev) => ({
      ...prev,
      quizData: prev.quizData.filter((q) => q.id !== questionId),
      totalMarks: prev.totalMarks - 1, // Decrement total marks
    }))
  }

  // Update the handleUpdateSubtopic function to handle type changes and JSON to PDF conversion
  const handleUpdateSubtopic = async (e) => {
    e.preventDefault()
    if (!editingSubtopicData || !currentTopic) {
      showToast("Missing subtopic data", "error")
      return
    }
    const {
      title,
      description,
      type,
      typeChanged,
      hasFileChanged,
      newFile,
      videoType,
      youtubeUrl,
      s3Uri,
      pdfUrl,
      quizData,
      totalMarks,
    } = editingSubtopicData
    // Validate file upload requirement
    if (typeChanged || (hasFileChanged && !newFile)) {
      if ((type === "video" || type === "pdf") && !newFile && (videoType !== "youtube" || type !== "video")) {
        showToast(`Please upload a ${type === "video" ? "video" : "PDF"} file`, "error")
        return
      }
    }
    setIsUpdatingSubtopic(true)
    setUploadProgress(0)
    try {
      let uploadResult = null
      let presignedUrl = null
      if ((hasFileChanged || typeChanged) && newFile) {
        const teacherId = localStorage.getItem("teacherId")
        const classRoomId = localStorage.getItem("classRoomId")
        if (!teacherId || !classRoomId) {
          throw new Error("Teacher ID or Classroom ID not found")
        }
        const allTopics = [...course.topics, ...unsavedTopics]
        const currentTopicObj = allTopics.find((t) => t.id === currentTopic)
        if (!currentTopicObj) {
          throw new Error("Chapter not found")
        }
        setUploadProgress(10)
        uploadResult = await postTeacherElearningCourseVideo(
          newFile,
          teacherId,
          classRoomId,
          newFile.name,
          course.name,
          currentTopicObj.title,
          title,
        )
        setUploadProgress(70)
        if (!uploadResult || !uploadResult.uri) {
          throw new Error(`Failed to upload ${type === "video" ? "video" : "PDF"}`)
        }
        try {
          presignedUrl = await generatePresignedUrl(uploadResult.uri)
          setUploadProgress(90)
        } catch (error) {
          console.error("Error generating presigned URL:", error)
        }
      }
      // Always update title and description
      const updates = { title, description }
      // Conditionally add type
      if (typeChanged) {
        updates.type = type
      }
      // Add content-specific data
      if (type === "video") {
        if (videoType === "youtube") {
          updates.video = {
            url: youtubeUrl,
            isYoutube: true,
          }
        } else if ((hasFileChanged || typeChanged) && uploadResult) {
          updates.video = {
            url: uploadResult.uri,
            isYoutube: false,
          }
        } else if (s3Uri && !typeChanged) {
          updates.video = {
            url: s3Uri,
            isYoutube: false,
          }
        }
      } else if (type === "pdf") {
        if ((hasFileChanged || typeChanged) && uploadResult) {
          updates.pdf = {
            url: uploadResult.uri,
          }
        } else if (pdfUrl && !typeChanged) {
          updates.pdf = {
            url: pdfUrl,
          }
        }
      } else if (type === "quiz" && quizData) {
        updates.quiz = {
          data: JSON.stringify(quizData),
          totalMarks,
        }
      }
      const payload = {
        request: "edit-subtopic",
        body: {
          courseId: courseId,
          topicId: currentTopic,
          subtopicId: editingSubtopicData.id,
          updates,
        },
      }
      const response = await postAws("https://api-dev.aurait.ai/eLearning", "POST", payload)
      setUploadProgress(100)
      if (response.statusCode === 200) {
        updateCourse((prev) => ({
          ...prev,
          topics: prev.topics.map((topic) => {
            if (topic.id === currentTopic) {
              return {
                ...topic,
                content: topic.content.map((item) => {
                  if (item.id === editingSubtopicData.id) {
                    const updatedItem = {
                      ...item,
                      title,
                      description,
                      type,
                    }
                    if (type === "video") {
                      if (videoType === "youtube") {
                        updatedItem.isYoutubeVideo = true
                        updatedItem.url = youtubeUrl
                        updatedItem.s3Uri = youtubeUrl
                        updatedItem.youtubeVideoId = extractYoutubeVideoId(youtubeUrl)
                      } else if ((hasFileChanged || typeChanged) && uploadResult) {
                        updatedItem.isYoutubeVideo = false
                        updatedItem.s3Uri = uploadResult.uri
                        updatedItem.url = presignedUrl || uploadResult.url
                      }
                    } else if (type === "pdf" && (hasFileChanged || typeChanged) && uploadResult) {
                      updatedItem.s3Uri = uploadResult.uri
                      updatedItem.url = presignedUrl || uploadResult.url
                    } else if (type === "quiz") {
                      updatedItem.quizData = quizData
                      updatedItem.totalMarks = totalMarks
                    }
                    return updatedItem
                  }
                  return item
                }),
              }
            }
            return topic
          }),
        }))
        showToast("Content updated successfully")
        closeEditSubtopicModal()
      } else {
        throw new Error(response.body?.message || "Failed to update content")
      }
    } catch (error) {
      console.error("Error updating content:", error)
      showToast("Error updating content. Please try again.", "error")
      setUploadProgress(0)
    } finally {
      setIsUpdatingSubtopic(false)
    }
  }

  // Add this function to handle generating descriptions with AI
  const generateDescriptionWithAI = async (topicId, subtopicId, subtopicTitle) => {
    try {
      // Show loading state
      updateCourse((prev) => ({
        ...prev,
        topics: prev.topics.map((topic) => {
          if (topic.id === topicId) {
            return {
              ...topic,
              content: topic.content.map((item) => {
                if (item.id === subtopicId) {
                  return {
                    ...item,
                    isGeneratingDescription: true,
                  }
                }
                return item
              }),
            }
          }
          return topic
        }),
      }))
      // Call the API to generate a description
      const response = await postAws("https://api-dev.aurait.ai/eLearning", "POST", {
        request: "create-subtopic-description",
        body: {
          courseId: courseId,
          topicId: topicId,
          subtopicId: subtopicId,
          subtopicTitle: subtopicTitle,
        },
      })
      if (response.statusCode === 200) {
        const responseBody = JSON.parse(response.body)
        const generatedDescription = responseBody.description || "No description available."
        // Update the subtopic with the generated description
        const updateResponse = await postAws("https://api-dev.aurait.ai/eLearning", "POST", {
          request: "edit-subtopic",
          body: {
            courseId: courseId,
            topicId: topicId,
            subtopicId: subtopicId,
            updates: {
              description: generatedDescription,
            },
          },
        })
        if (updateResponse.statusCode === 200) {
          // Update the course state with the new description
          updateCourse((prev) => ({
            ...prev,
            topics: prev.topics.map((topic) => {
              if (topic.id === topicId) {
                return {
                  ...topic,
                  content: topic.content.map((item) => {
                    if (item.id === subtopicId) {
                      return {
                        ...item,
                        description: generatedDescription,
                        isGeneratingDescription: false,
                      }
                    }
                    return item
                  }),
                }
              }
              return topic
            }),
          }))
          showToast("Description generated successfully")
        } else {
          throw new Error(updateResponse.body?.message || "Failed to update description")
        }
      } else {
        throw new Error(response.body?.message || "Failed to generate description")
      }
    } catch (error) {
      console.error("Error generating description:", error)
      showToast("Error generating description. Please try again.", "error")
      // Reset the loading state
      updateCourse((prev) => ({
        ...prev,
        topics: prev.topics.map((topic) => {
          if (topic.id === topicId) {
            return {
              ...topic,
              content: topic.content.map((item) => {
                if (item.id === subtopicId) {
                  return {
                    ...item,
                    isGeneratingDescription: false,
                  }
                }
                return item
              }),
            }
          }
          return topic
        }),
      }))
    }
  }

  // Add this function to the ContentManagement component, right after the generateDescriptionWithAI function
  // Function to generate a course from YouTube playlist
  const generateCourseFromYouTubePlaylist = async (playlistUrl) => {
    // Validate the playlist URL
    if (!playlistUrl || !playlistUrl.includes("youtube.com/playlist?list=")) {
      showToast("Please enter a valid YouTube playlist URL", "error")
      return
    }
    try {
      // Show loading state
      showToast("Generating course from YouTube playlist. This may take a minute...", "info")
      // Call the API to fetch playlist data
      const response = await postAws(
        "https://api-dev.aurait.ai/playlistFromYoutube",
        "POST",
        JSON.parse(
          JSON.stringify({
            playlist_url: playlistUrl,
          }),
        ),
      )
      if (!response || response.statusCode !== 200) {
        throw new Error("Failed to fetch playlist data")
      }
      const data = JSON.parse(response.body || "{}")
      if (!data.playlist || !data.videos || !Array.isArray(data.videos) || data.videos.length === 0) {
        throw new Error("Invalid playlist data received")
      }
      // Create a new topic (chapter) for the playlist
      const topicResponse = await postAws("https://api-dev.aurait.ai/eLearning", "POST", {
        request: "add-topic",
        body: {
          courseId: courseId,
          topicTitle: data.playlist.title || "YouTube Playlist",
        },
      })
      if (topicResponse.statusCode !== 200) {
        throw new Error("Failed to create topic from playlist")
      }
      const topicData = JSON.parse(topicResponse.body)
      const topicId = topicData.topic.id
      // Add videos as subtopics
      let addedVideos = 0
      for (const video of data.videos) {
        // Skip private videos
        if (video.title === "Private video" || !video.url || !video.video_id) {
          continue
        }
        try {
          // Add the video as a subtopic
          const subtopicResponse = await postAws("https://api-dev.aurait.ai/eLearning", "POST", {
            request: "add-subtopic",
            body: {
              courseId: courseId,
              topicId: topicId,
              subtopicTitle: video.title,
              subtopicDescription: video.description || "",
              videoUrl: video.url,
              contentType: "video",
              isYoutubeVideo: true,
            },
          })
          if (subtopicResponse.statusCode === 200) {
            addedVideos++
          }
        } catch (subtopicError) {
          console.error("Error adding video subtopic:", subtopicError)
          // Continue with other videos even if one fails
        }
      }
      // Refresh course details to show the new structure
      await fetchCourseDetails()
      showToast(`Course generated successfully with ${addedVideos} videos`, "success")
    } catch (error) {
      console.error("Error generating course from YouTube playlist:", error)
      showToast("Error generating course. Please try again.", "error")
    }
  }

  // Render content based on type
  const renderContent = (item) => {
    // Check if this is a JSON file that needs to be converted to PDF
    const shouldConvertToPdf = item.type === "pdf" && item.s3Uri && item.s3Uri.includes(".json")
    // If it's a JSON file, show conversion button or convert automatically
    if (shouldConvertToPdf && !isConvertingJsonToPdf) {
      // Auto-convert on first render
      setTimeout(() => {
        checkAndConvertJsonToPdf(item, activeContentTab.topicId)
      }, 100)
    }

    switch (item.type) {
      case "video":
        if (item.isYoutubeVideo && item.youtubeVideoId) {
          return (
            <div className="space-y-4">
              {!previewMode && (
                <div className="flex justify-end space-x-2">
                  <button
                    onClick={() => handleCreateNotesFromVideo(item.youtubeVideoId, activeContentTab.topicId, item.id)}
                    className="px-3 py-1 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors inline-flex items-center text-sm"
                  >
                    <BookOpen className="h-4 w-4 mr-1" />
                    Create Notes
                  </button>
                  <button
                    onClick={() => openEditSubtopicModal(activeContentTab.topicId, item)}
                    className="px-3 py-1 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors inline-flex items-center text-sm"
                  >
                    <Edit2 className="h-4 w-4 mr-1" />
                    Edit Content
                  </button>
                </div>
              )}
              <div
                className="w-full"
                style={{ position: "relative", paddingBottom: "56.25%", height: 0, overflow: "hidden" }}
              >
                <iframe
                  src={`https://www.youtube.com/embed/${item.youtubeVideoId}`}
                  title={item.title}
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  style={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%" }}
                  className="rounded-lg shadow-lg"
                ></iframe>
              </div>
            </div>
          )
        } else {
          return (
            <div className="space-y-4">
              {!previewMode && (
                <div className="flex justify-end">
                  <button
                    onClick={() => openEditSubtopicModal(activeContentTab.topicId, item)}
                    className="px-3 py-1 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors inline-flex items-center text-sm"
                  >
                    <Edit2 className="h-4 w-4 mr-1" />
                    Edit Content
                  </button>
                </div>
              )}
              <div className="aspect-w-16 aspect-h-9 rounded-lg overflow-hidden shadow-lg bg-black">
                <video src={item.url} controls className="w-full h-full">
                  Your browser does not support the video tag.
                </video>
              </div>
            </div>
          )
        }
      case "pdf":
        // Check if notes are being created (no content URL but notesStatus is creating/in-progress)
        if (!item.url && !item.s3Uri && (item.quizStatus === "creating" || item.notesStatus === "in-progress")) {
          return (
            <div className="space-y-4">
              {!previewMode && (
                <div className="flex justify-end">
                  <button
                    onClick={() => openEditSubtopicModal(activeContentTab.topicId, item)}
                    className="px-3 py-1 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors inline-flex items-center text-sm"
                  >
                    <Edit2 className="h-4 w-4 mr-1" />
                    Edit Content
                  </button>
                </div>
              )}
              <div className="rounded-lg overflow-hidden shadow-lg h-[calc(100vh-280px)]">
                <div className="flex flex-col items-center justify-center h-full bg-gradient-to-br from-green-50 to-blue-50 text-center">
                  <div className="relative w-20 h-20 mx-auto mb-6">
                    <div className="absolute inset-0 rounded-full border-4 border-green-200 border-opacity-50"></div>
                    <div className="absolute inset-0 rounded-full border-4 border-green-500 border-t-transparent animate-spin"></div>
                    <div className="absolute inset-0 flex items-center justify-center">
                      <Loader className="h-8 w-8 text-green-600" />
                    </div>
                  </div>
                  <h3 className="text-xl font-semibold text-green-800 mb-3">AI is creating notes for you</h3>
                  <p className="text-green-700 mb-4 max-w-md mx-auto">
                    Please wait while we generate comprehensive notes from your content. This usually takes 2-3 minutes.
                  </p>
                  <div className="w-full max-w-md mx-auto bg-white h-2 rounded-full overflow-hidden">
                    <div className="h-full bg-gradient-to-r from-green-500 to-blue-500 animate-pulse"></div>
                  </div>
                </div>
              </div>
            </div>
          )
        }
        return (
          <div className="space-y-4">
            {!previewMode && (
              <div className="flex justify-end space-x-2">
                {/* Show Edit button for AI-generated notes */}
                {isAIGeneratedNote(item) && (
                  <button
                    onClick={() => openNotesEditor(item)}
                    className="px-3 py-1 bg-purple-600 text-white rounded-md hover:bg-purple-700 transition-colors inline-flex items-center text-sm"
                  >
                    <Edit2 className="h-4 w-4 mr-1" />
                    Edit Notes
                  </button>
                )}
                {shouldConvertToPdf && (
                  <button
                    onClick={() => checkAndConvertJsonToPdf(item, activeContentTab.topicId)}
                    disabled={isConvertingJsonToPdf}
                    className="px-3 py-1 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors inline-flex items-center text-sm disabled:opacity-50"
                  >
                    {isConvertingJsonToPdf ? <Loader className="h-4 w-4 mr-1 animate-spin" /> : "Convert to PDF"}
                  </button>
                )}
                <button
                  onClick={() => openEditSubtopicModal(activeContentTab.topicId, item)}
                  className="px-3 py-1 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors inline-flex items-center text-sm"
                >
                  <Edit2 className="h-4 w-4 mr-1" />
                  Edit Content
                </button>
              </div>
            )}
            <div className="rounded-lg overflow-hidden shadow-lg h-[calc(100vh-280px)]">
              {isConvertingJsonToPdf ? (
                <div className="flex flex-col items-center justify-center h-full bg-gradient-to-br from-green-50 to-blue-50 text-center">
                  <div className="relative w-20 h-20 mx-auto mb-6">
                    <div className="absolute inset-0 rounded-full border-4 border-green-200 border-opacity-50"></div>
                    <div className="absolute inset-0 rounded-full border-4 border-green-500 border-t-transparent animate-spin"></div>
                    <div className="absolute inset-0 flex items-center justify-center">
                      <Loader className="h-8 w-8 text-green-600" />
                    </div>
                  </div>
                  <h3 className="text-xl font-semibold text-green-800 mb-3">Converting Notes to PDF</h3>
                  <p className="text-green-700 mb-4 max-w-md mx-auto">
                    We're converting your JSON notes into a beautifully formatted PDF document.
                  </p>
                  <div className="w-full max-w-md mx-auto bg-white h-2 rounded-full overflow-hidden">
                    <div className="h-full bg-gradient-to-r from-green-500 to-blue-500 animate-pulse"></div>
                  </div>
                </div>
              ) : shouldConvertToPdf ? (
                <div className="flex flex-col items-center justify-center h-full bg-gradient-to-br from-yellow-50 to-orange-50 text-center">
                  <h3 className="text-xl font-semibold text-orange-800 mb-3">JSON Notes Detected</h3>
                  <p className="text-orange-700 mb-4 max-w-md mx-auto">
                    This content contains JSON notes that can be converted to a beautifully formatted PDF.
                  </p>
                  <button
                    onClick={() => checkAndConvertJsonToPdf(item, activeContentTab.topicId)}
                    className="px-6 py-3 bg-orange-600 text-white rounded-md hover:bg-orange-700 transition-colors"
                  >
                    Convert to PDF
                  </button>
                </div>
              ) : (
                <iframe src={item.url} className="w-full h-full" title={item.title}>
                  Your browser does not support embedded PDFs.
                </iframe>
              )}
            </div>
          </div>
        )
     case "quiz":
  return (
    <div className="space-y-4">
      {!previewMode && (
        <div className="flex justify-end space-x-2">
          <button
            onClick={() => openEditSubtopicModal(activeContentTab.topicId, item)}
            className="px-3 py-1 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition-colors inline-flex items-center text-sm"
          >
            <Edit2 className="h-4 w-4 mr-1" />
            Edit Details
          </button>
          <button
            onClick={() => selectContent(activeContentTab.topicId, item.id, "edit-quiz")}
            className="px-3 py-1 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors inline-flex items-center text-sm"
          >
            <Edit2 className="h-4 w-4 mr-1" />
            Edit Quiz Questions
          </button>
        </div>
      )}

      <div className="bg-white p-6 rounded-lg shadow-lg">
        <h4 className="font-medium text-xl mb-6">{item.title}</h4>
        {item.description && <p className="text-gray-600 mb-8">{item.description}</p>}

        {/* ✅ Show quiz generation UI based on status */}
        {item.quizStatus === "generating" ? (
          <div className="flex flex-col items-center justify-center py-12 bg-yellow-50 rounded-lg border-2 border-dashed border-yellow-300">
            <Loader className="h-12 w-12 animate-spin text-yellow-600 mb-4" />
            <h3 className="text-lg font-medium text-yellow-800 mb-2">AI is creating your quiz</h3>
            <p className="text-yellow-600 text-center max-w-md">
              Please wait while we generate quiz questions based on your selected content. This usually takes 1-2
              minutes.
            </p>
          </div>
        ) : (
          <div className="space-y-8">
            {(() => {
              let parsedQuestions = []
              try {
                if (typeof item.quizData === "string") {
                  parsedQuestions = JSON.parse(item.quizData)
                } else if (Array.isArray(item.quizData)) {
                  parsedQuestions = item.quizData
                }
              } catch (error) {
                console.error("Error parsing quiz data:", error)
              }

              if (parsedQuestions.length > 0) {
                return parsedQuestions.map((question, idx) => {
                  const questionText = question.question || question.text || ""
                  const options = question.options || []
                  const correctAnswer = question.correctAnswer || ""
                  const questionType = question.questionType || "multiple-choice"
                  const explanation = question.explanation || ""

                  return (
                    <div key={idx} className="bg-gray-50 p-5 rounded-lg">
                      <div className="flex justify-between">
                        <p className="font-medium text-lg mb-4">
                          {idx + 1}. {questionText}
                        </p>
                        <span className="text-xs text-gray-500 bg-gray-200 px-2 py-1 rounded-full">
                          {questionType}
                        </span>
                      </div>

                      <div className="space-y-3 ml-4">
                        {(questionType === "true-false" || questionType === "multiple-choice") &&
                          options.map((option, optIdx) => {
                            const optionText = typeof option === "string" ? option : option.text || ""
                            const isCorrect = option.isCorrect || optionText === correctAnswer

                            return (
                              <div key={optIdx} className="flex items-center space-x-3">
                                <div
                                  className={`w-5 h-5 rounded-full border flex items-center justify-center ${
                                    isCorrect ? "bg-green-500 border-green-500" : "border-gray-300"
                                  }`}
                                >
                                  {isCorrect && <div className="w-2 h-2 bg-white rounded-full"></div>}
                                </div>
                                <p className={isCorrect ? "font-medium" : ""}>{optionText}</p>
                              </div>
                            )
                          })}

                        {questionType === "short-answer" && (
                          <div className="mt-2 p-3 bg-blue-50 rounded-md">
                            <p className="font-medium text-blue-800">Expected Answer:</p>
                            <p className="text-blue-700 mt-1">{correctAnswer}</p>
                          </div>
                        )}
                      </div>

                      {explanation && (
                        <div className="mt-3 pt-3 border-t border-gray-200 text-sm text-gray-600">
                          <p className="font-medium text-gray-700">Explanation:</p>
                          <p>{explanation}</p>
                        </div>
                      )}

                      {question.marks && (
                        <div className="mt-2 text-xs text-gray-500 text-right">Marks: {question.marks}</div>
                      )}
                    </div>
                  )
                })
              } else {
                return (
                  <div className="bg-yellow-50 p-6 rounded-lg text-center">
                    <p className="text-yellow-800 font-medium text-lg mb-2">No Questions Available</p>
                    <p className="text-yellow-700">
                      This quiz doesn't have any questions yet. You can add questions by clicking the "Edit Quiz
                      Questions" button above.
                    </p>
                  </div>
                )
              }
            })()}
          </div>
        )}
      </div>
    </div>
  )

      default:
        return <div>Unknown content type</div>
    }
  }

  // Modify the return object to include the new function
  return {
    openContentMenu,
    openContentModal,
    closeContentModal,
    handleVideoSubmit,
    handlePdfSubmit,
    handleQuizSubmit,
    handleDeleteSubtopic,
    openEditSubtopicModal,
    closeEditSubtopicModal,
    handleContentTypeChange,
    handleEditFileChange,
    handleEditQuizQuestionChange,
    handleEditQuizOptionChange,
    addEditQuizQuestion,
    removeEditQuizQuestion,
    handleUpdateSubtopic,
    renderContent,
    handleVideoFileChange,
    handlePdfFileChange,
    handleQuizQuestionChange,
    handleQuizOptionChange,
    addQuizQuestion,
    removeQuizQuestion,
    isContentModalOpen,
    contentType,
    videoData,
    setVideoData,
    pdfData,
    setPdfData,
    quizData,
    setQuizData,
    isEditingSubtopic,
    editingSubtopicData,
    setEditingSubtopicData,
    isUpdatingSubtopic,
    uploadProgress,
    addingSubtopicId,
    showContentMenu,
    setShowContentMenu,
    fileInputRef,
    pdfInputRef,
    youtubeInputRef,
    editFileInputRef,
    generateDescriptionWithAI,
    handleUpdateEditingData,
    generateCourseFromYouTubePlaylist,
    checkAndConvertJsonToPdf,
    isConvertingJsonToPdf,
    // New exports for notes editing
    isEditingNotes,
    editingNotesData,
    notesJsonData,
    isLoadingNotesJson,
    isSavingNotes,
    openNotesEditor,
    closeNotesEditor,
    updateNotesContent,
    updateNotesTitle,
    saveEditedNotes,
    isAIGeneratedNote,
  }
}
